import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { SwRegistrationOptions, provideServiceWorker } from '@angular/service-worker';

import { routes } from './app.routes';
import { moduleDisabledInterceptor } from './core/api/module-disabled.interceptor';
import { tenantHeaderInterceptor } from './core/api/tenant-header.interceptor';
import { authInterceptor } from './core/auth/auth.interceptor';
import { DevTenantSwitcherService } from './core/config/dev-tenant-switcher.service';
import { demoBackendInterceptor } from './core/demo/demo-backend.interceptor';
import { DemoModeService } from './core/demo/demo-mode.service';
import { initializeMode } from './core/mode/mode.initializer';
import { ModeService } from './core/mode/mode.service';
import { initializeTenantContext } from './core/tenant-context/tenant-bootstrap.initializer';
import { TenantContextService } from './core/tenant-context/tenant-context.service';
import {
  PageTransitionService,
  mobileShellViewTransition,
} from './core/navigation/page-transition.service';
import { SwipeBackService } from './core/navigation/swipe-back.service';
import { SeoService } from './shared/seo.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withViewTransitions({
        skipInitialTransition: true,
        onViewTransitionCreated: mobileShellViewTransition,
      }),
    ),
    provideHttpClient(
      withInterceptors([
        demoBackendInterceptor,
        authInterceptor,
        tenantHeaderInterceptor,
        moduleDisabledInterceptor,
      ]),
    ),
    provideAppInitializer(() => {
      const init = initializeMode(inject(ModeService), inject(DevTenantSwitcherService));
      init();
    }),
    provideAppInitializer(() => {
      inject(DemoModeService).bootstrap();
    }),
    provideAppInitializer(() => {
      const init = initializeTenantContext(inject(ModeService), inject(TenantContextService));
      init();
    }),
    provideAppInitializer(() => {
      inject(SeoService).init();
    }),
    provideAppInitializer(() => {
      inject(PageTransitionService).init();
    }),
    provideAppInitializer(() => {
      inject(SwipeBackService).init();
    }),
    provideServiceWorker('custom-sw.js'),
    {
      provide: SwRegistrationOptions,
      useFactory: (): SwRegistrationOptions => ({
        enabled:
          !isDevMode() &&
          inject(ModeService).isTenant() &&
          !inject(DemoModeService).isDemoActive(),
        registrationStrategy: 'registerWhenStable:30000',
      }),
    },
  ],
};
